import React, { useEffect, useRef, useState } from 'react';
import BubbleCollection from './collections/BubbleCollection';
import BubbleModel from './models/BubbleModel';

const BackbonePanel = () => {
  const collectionRef = useRef(null);
  const [bubbles, setBubbles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [predictedSizes, setPredictedSizes] = useState({});

  // Initialize Backbone collection
  useEffect(() => {
    collectionRef.current = new BubbleCollection();

    // Set up event listeners for collection changes
    const collection = collectionRef.current;

    const updateState = () => {
      setBubbles(collection.toJSON());
      setStats(collection.getTeamStats());
    };

    // Listen for collection events
    collection.on('add remove reset change', updateState);
    collection.on('sync', () => {
      setLoading(false);
      setError(null);
      updateState();
    });
    collection.on('error', (model, response) => {
      setLoading(false);
      setError(response.responseJSON?.error || 'Network error');
    });

    // Initial fetch
    fetchBubbles();

    // Cleanup on unmount
    return () => {
      collection.off();
    };
  }, []); // Empty dependency - run only once

  // Separate useEffect for auto-refresh
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      if (collectionRef.current && !loading) {
        fetchBubbles();
      }
    }, 10000); // 10 seconds

    return () => {
      clearInterval(refreshInterval);
    };
  }, []); // Empty dependency - create timer only once

  // Real-time size prediction timer
  useEffect(() => {
    const predictionTimer = setInterval(() => {
      if (collectionRef.current && bubbles.length > 0) {
        const newPredictedSizes = {};
        collectionRef.current.each(model => {
          const bubbleId = model.get('id');
          const predictedSize = model.getCurrentSize();
          newPredictedSizes[bubbleId] = predictedSize;
        });
        setPredictedSizes(newPredictedSizes);
      }
    }, 50); // Update every 1/20 second for smoother deflation

    return () => {
      clearInterval(predictionTimer);
    };
  }, [bubbles]); // Re-create timer when bubbles change

  const fetchBubbles = () => {
    setLoading(true);
    setError(null);
    collectionRef.current.fetch();
  };

  const createBubble = () => {
    const name = prompt('Enter bubble name:');
    if (!name) return;

    const newBubble = new BubbleModel({
      name: name.trim(),
      size: 120,
      xPercent: Math.random(),
      yPercent: Math.random()
    });

    setLoading(true);
    newBubble.save(null, {
      success: (model, response) => {
        console.log('Bubble creation response:', response);
        if (response && response.debug_info) {
          console.log('Debug info:', response.debug_info);
        }
        // Only add to collection if server responded with created bubble data
        if (response && response.bubble_id) {
          // Update model with server response (including proper ID)
          model.set('id', response.bubble_id);
          collectionRef.current.add(model);
          // Refresh to get the full bubble data from server
          fetchBubbles();
        }
        setLoading(false);
      },
      error: (model, response) => {
        console.error('Bubble creation error:', response);
        setError(response.responseJSON?.error || response.statusText || 'Failed to create bubble');
        setLoading(false);
      }
    });
  };

  const inflateBubble = (id) => {
    const bubble = collectionRef.current.get(id);
    if (bubble) {
      setLoading(true);
      bubble.inflate().then(() => {
        // The save() in inflate() already updated the model with server response
        // Just trigger a state update without fetching
        setLoading(false);
      }).catch(error => {
        setError('Failed to inflate bubble');
        setLoading(false);
      });
    }
  };

  const deflateBubble = (id) => {
    const bubble = collectionRef.current.get(id);
    if (bubble) {
      setLoading(true);
      bubble.deflate().then(() => {
        setLoading(false);
      }).catch(error => {
        setError('Failed to deflate bubble');
        setLoading(false);
      });
    }
  };

  const deleteBubble = (id) => {
    const bubble = collectionRef.current.get(id);
    if (bubble && window.confirm(`Delete bubble "${bubble.get('name')}"?`)) {
      setLoading(true);
      bubble.destroy({
        success: () => {
          setLoading(false);
        },
        error: () => {
          setError('Failed to delete bubble');
          setLoading(false);
        }
      });
    }
  };

  return (
    <div className="backbone-panel">
      <div className="panel-header">
        <h3>🧪 Backbone.js MVP</h3>
        <button onClick={fetchBubbles} disabled={loading}>
          {loading ? '⟳' : '🔄'} Refresh
        </button>
        <button onClick={createBubble} disabled={loading}>
          ➕ Create Bubble
        </button>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      <div className="stats-section">
        <h4>📊 Statistics</h4>
        {stats && (
          <div className="stats-grid">
            <div>Total Bubbles: <strong>{bubbles.length}</strong></div>
            <div>Total Size: <strong>{collectionRef.current?.getTotalSize() || 0}</strong></div>
            {Object.entries(stats).map(([team, data]) => (
              <div key={team}>
                Team {team}: <strong>{data.count}</strong> bubbles
                (avg: {data.averageSize.toFixed(1)})
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bubbles-list">
        <h4>🫧 Bubbles ({bubbles.length})</h4>
        {bubbles.length === 0 ? (
          <p className="empty-state">No bubbles found. Create one!</p>
        ) : (
          <div className="bubble-items">
            {bubbles.map((bubble) => (
              <div key={bubble.id} className="bubble-item">
                <div className="bubble-info">
                  <div className="bubble-name">{bubble.name}</div>
                  <div className="bubble-details">
                    Size: <strong>{predictedSizes[bubble.id] || bubble.size}</strong> |
                    Team: <span style={{color: bubble.color}}>{bubble.team}</span>
                  </div>
                </div>
                <div className="bubble-actions">
                  <button
                    onClick={() => inflateBubble(bubble.id)}
                    disabled={loading}
                    title="Inflate +1"
                  >
                    ⬆️
                  </button>
                  <button
                    onClick={() => deflateBubble(bubble.id)}
                    disabled={loading}
                    title="Deflate -1"
                  >
                    ⬇️
                  </button>
                  <button
                    onClick={() => deleteBubble(bubble.id)}
                    disabled={loading}
                    title="Delete"
                    className="delete-btn"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="panel-footer">
        <p>Panel version: predict size</p>
        <small>
          Using Backbone.js → {process.env.REACT_APP_BACKBONE_URL || 'https://ppb.chatforest.com'}/api/bubbles
        </small>
      </div>
    </div>
  );
};

export default BackbonePanel;
